import { forwardRef } from 'react';
import type { DragEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import './InlineList.css';

interface InlineListProps {
  children: ReactNode;
  className?: string;
  compact?: boolean;
  onDragOver?: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (event: DragEvent<HTMLDivElement>) => void;
  onDrop?: (event: DragEvent<HTMLDivElement>) => void;
}

interface InlineListRowProps {
  children: ReactNode;
  actions?: ReactNode;
  onClick?: () => void;
  dragHandle?: ReactNode;
  className?: string;
  onDragOver?: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (event: DragEvent<HTMLDivElement>) => void;
  onDrop?: (event: DragEvent<HTMLDivElement>) => void;
  rowRef?: (node: HTMLDivElement | null) => void;
}

export const InlineList = forwardRef<HTMLDivElement, InlineListProps>(
  ({ children, className = '', compact = false, onDragOver, onDragLeave, onDrop }, ref) => {
    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      onDragOver?.(event);
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      onDrop?.(event);
    };

    return (
      <div
        ref={ref}
        className={`inline-list ${compact ? 'inline-list--compact' : ''} ${className}`.trim()}
        onDragOver={handleDragOver}
        onDragLeave={onDragLeave}
        onDrop={handleDrop}
      >
        {children}
      </div>
    );
  }
);
InlineList.displayName = 'InlineList';

export const InlineListRow = ({ children, actions, onClick, dragHandle, className = '', onDragOver, onDragLeave, onDrop, rowRef }: InlineListRowProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onClick) return;

    const target = event.target as HTMLElement;
    if (
      target.closest('.inline-list-row-actions') ||
      target.closest('button, a, input, textarea, select')
    ) {
      return;
    }

    onClick();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    onDrop?.(event);
  };

  return (
    <div
      ref={rowRef}
      className={`inline-list-row ${onClick ? 'inline-list-row-clickable' : ''} ${className}`.trim()}
      onClick={handleRowClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="inline-list-row-content">
        {dragHandle}
        {children}
      </div>
      {actions && <div className="inline-list-row-actions">{actions}</div>}
    </div>
  );
};
